import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2, Clock, BookOpen, Trophy } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const StudyHistoryPage = () => {
  const { childId } = useParams();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/children/${childId}/study-history?limit=150`);
        setData(res.data);
      } catch (err) {
        toast.error('Could not load study history');
      } finally {
        setLoading(false);
      }
    })();
  }, [childId, api]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-emerald-50 to-white">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const items = data?.items || [];
  const bySubject = data?.by_subject || [];

  const relTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return 'just now';
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
      return d.toLocaleDateString();
    } catch { return ''; }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50/40 pb-16">
      <header className="p-4 border-b border-slate-100 bg-white/70 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to={`/learning-hub/${childId}`} className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700" data-testid="study-history-back">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Learning Hub</span>
          </Link>
          <div className="text-right">
            <p className="text-xs text-slate-500">Study log</p>
            <p className="font-fredoka text-lg font-bold text-slate-800">{data?.child_name}</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
            What {data?.child_name || 'your child'} has studied
          </h1>
          <p className="text-slate-600">
            {data?.total_completed || 0} completed activities · {data?.total_minutes || 0} minutes of focused practice
          </p>
        </motion.div>

        {bySubject.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="study-history-by-subject">
            {bySubject.slice(0, 8).map((row) => (
              <Card key={row.subject} className="border-emerald-100">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{row.count}</p>
                  <p className="text-xs text-slate-500 capitalize">{row.subject}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {items.length === 0 ? (
          <Card className="border-2 border-dashed border-slate-200 bg-white">
            <CardContent className="p-10 text-center">
              <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-slate-700 mb-1">Nothing here yet</p>
              <p className="text-xs text-slate-500 mb-4">Completed activities show up here right after they finish.</p>
              <Link to={`/learning-hub/${childId}`}>
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 rounded-full">Start learning</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="study-history-list">
            {items.map((it, i) => (
              <Card key={`${it.activity_id}-${i}`} className="border-slate-200 hover:border-emerald-300 transition" data-testid={`study-history-item-${i}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-2xl flex-shrink-0">
                    <span>{it.emoji}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-2 items-center">
                      <p className="font-semibold text-slate-800 truncate">{it.title}</p>
                      {it.kind === 'assessment' && (
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">Assessment</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 capitalize">
                      {it.subject}
                      {it.level ? ` · Level ${it.level}` : ''}
                      {it.rounds && it.rounds > 1 ? ` · ${it.rounds} rounds` : ''}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {typeof it.score === 'number' && (
                      <p className="text-sm font-bold text-emerald-700 flex items-center gap-1 justify-end">
                        <Trophy className="w-3.5 h-3.5" /> {it.score}%
                      </p>
                    )}
                    {it.duration_seconds > 0 && (
                      <p className="text-xs text-slate-500 flex items-center gap-1 justify-end">
                        <Clock className="w-3 h-3" /> {Math.round(it.duration_seconds / 60)}m
                      </p>
                    )}
                    <p className="text-xs text-slate-400 mt-0.5">{relTime(it.completed_at)}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default StudyHistoryPage;
